import type { V2Bag, V2Value } from "@/features/applications/api/applications.api";
import { type FieldDef } from "./model";
import { FieldInput } from "./FieldInput";
import { SectionCard } from "./primitives";

export function FieldGrid({
  fields,
  bag,
  onChange,
}: {
  fields: FieldDef[];
  bag: V2Bag;
  onChange: (key: string, value: V2Value) => void;
}) {

  const visible = fields.filter((d) => !d.showWhen || d.showWhen(bag));

  const groups: { section?: string; items: FieldDef[] }[] = [];
  for (const d of visible) {
    const last = groups[groups.length - 1];
    if (last && last.section === d.section) last.items.push(d);
    else groups.push({ section: d.section, items: [d] });
  }

  return (
    <div className="space-y-5">
      {groups.map((g, i) => (
        <div key={g.section ?? `g${i}`}>
          {g.section && (
            <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2 border-b border-slate-100 pb-1">
              {g.section}
            </h4>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {g.items.map((d) => (
              <FieldInput
                key={d.key}
                def={d}
                value={bag[d.key] ?? null}
                bag={bag}
                onChange={(v) => onChange(d.key, v)}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export function CardedFields({
  fields, bag, onChange,
}: {
  fields: FieldDef[];
  bag: V2Bag;
  onChange: (key: string, value: V2Value) => void;
}) {
  const groups: { section?: string; items: FieldDef[] }[] = [];
  for (const d of fields) {
    const last = groups[groups.length - 1];
    if (last && last.section === d.section) last.items.push(d);
    else groups.push({ section: d.section, items: [d] });
  }
  return (
    <>
      {groups.map((g, i) => (
        <SectionCard key={g.section ?? `g${i}`} title={g.section ?? "Details"}>
          <FieldGrid fields={g.items.map((d) => ({ ...d, section: undefined }))} bag={bag} onChange={onChange} />
        </SectionCard>
      ))}
    </>
  );
}

export function AddrCard({
  title, fields, bag, onChange,
}: {
  title: string;
  fields: FieldDef[];
  bag: V2Bag;
  onChange: (key: string, value: V2Value) => void;
}) {
  const find = (suffix: string) => fields.find((d) => d.key.toLowerCase().endsWith(suffix));
  const render = (d?: FieldDef) =>
    d ? <FieldInput key={d.key} def={d} value={bag[d.key] ?? null} bag={bag} onChange={(v) => onChange(d.key, v)} /> : null;
  return (
    <div className="border border-slate-200 rounded-lg p-4">
      <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">{title}</h4>
      <div className="grid grid-cols-1 gap-3">
        {render(find("name"))}
        {render(find("address"))}
        {render(find("suite"))}
        <div className="grid grid-cols-3 gap-3">
          {render(find("zip"))}
          {render(find("city"))}
          {render(find("state"))}
        </div>
        {render(find("phone"))}
      </div>
    </div>
  );
}
